<template>
  <v-card :flat="true" rounded="0" >
    <v-card-title v-if="mode == 'default'">
      <div class="row">
        <div class="col">
          <div class="headline" style="cursor: pointer;" @click="changeRuleName()">
            {{ calculation.name }}
            <v-icon style="font-size: inherit;" class="ml-1">mdi-pencil</v-icon>
          </div>
        </div>
        <div class="col text-right">
          <help-text-btn name="CALCULATION_SUBSITE" />
        </div>
      </div>
    </v-card-title>

    <v-card-text :class="`${mode == 'default'? 'pb-16': 'pb-2'} `">
      <div class="row" v-if="mode == 'default'">
        <v-spacer></v-spacer>

        <div class="col-auto">
          <v-btn :loading="isGettingTestResults" :disabled="isGettingTestResults && isSaving" class="general-action-btn" @click="ruleTest()">
            <v-icon left>mdi-ab-testing</v-icon>
            <lang t="DERIVATION-RULE-BTN-TEST"></lang>
          </v-btn>

          <v-btn :loading="isSaving" class="general-action-btn" @click="ruleSave()">
            <v-icon left>mdi-content-save</v-icon>
            <lang t="DERIVATION-RULE-BTN-SAVE"></lang>
          </v-btn>

          <v-btn :loading="isPublishing" :disabled="isSaving" class="general-action-btn" @click="rulePublish()">
            <v-icon left>mdi-publish</v-icon>
            <lang t="DERIVATION-RULE-BTN-PUBLISH"></lang
          ></v-btn>

          <v-btn :disabled="isSaving" text @click="$emit('closeModal')">
            <lang t="GLOBAL-BTN-CLOSE"></lang>
          </v-btn>
        </div>
      </div>

      <template v-if="mode == 'testing'">
        <template v-if="!testResult">
          <div class="text-right mt-3 mb-0">
            <v-btn text @click="$emit('closeModal')">
            <lang t="GLOBAL-BTN-CLOSE"></lang>
          </v-btn>
          </div>
        </template>
        <template v-else>
          <template v-if="!Array.isArray(testResult.result)">
            <div class="row">
            <div class="col">
          <div class="text-h6 text-center">
            <template v-if="testResult.done"> Result: {{ testResult.result }} </template>
            <template v-else>
              Result: Error
            </template>
          </div>
        </div>
          
        </div> 
        </template>

        <template v-else>
          <div class="text-h6 text-center" v-if="testResult.explanation">
            Result (Done
            {{ Math.ceil(testResult.explanation.donePercent * 100) }}%)
          </div>

          <div class="testResultBoxWrapper">
            <div class="testResultBox" v-for="(res, index) in testResult.result" :key="index">
              <div>
                {{ res.result }}
              </div>

              <div>{{ (res.percent * 100).toFixed(1) }}%</div>
            </div>
          </div> 
        </template>  
        </template>
        
      </template>

      <!-- loop stages -->
      <div class="my-4 mb-8 stage grey lighten-5 rounded-lg" style="position:relative" v-for="(stage, stageIndex) in rules.stages" :key="stage.name">
        <div class="stageHeader py-2 px-4 d-flex">
          <div class="stageName" @click="changeStageName(stage)">
            {{ stage.name }}
            <v-icon style="font-size: inherit;" class="ml-1" v-if="mode == 'default'">mdi-pencil</v-icon>
          </div>

          <v-btn title="delete stage" class="ml-auto" text plain x-small @click="deleteStage(stageIndex)" v-if="mode == 'default' && stageIndex != 0">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>

        <!-- loop through steps of a stage -->
        <draggable v-model="stage.steps" :disabled="mode === 'testing'">
          <div v-for="(step, stepIndex) in stage.steps" :key="stepIndex">
            <div class="row px-2 py-4">
              <div class="col flex-grow-0 align-items">
                <template v-if="mode == 'default'">
                  <v-speed-dial direction="right" open-on-hover transition="slide-x-reverse-transition">
                    <template v-slot:activator>
                      <v-btn elevation="0" x-small fab color="themeColor">
                        {{ stepIndex + 1 }}
                      </v-btn>
                    </template>

                    <v-btn title="delete step" x-small fab @click.stop.prevent="deleteStep(stage.steps, stepIndex)" v-if="mode == 'default'">
                      <v-icon x-small>mdi-delete</v-icon>
                    </v-btn>
                  </v-speed-dial>
                </template>

                <template v-else>
                  <v-btn elevation="0" x-small fab color="themeColor">
                    {{ stepIndex + 1 }}
                  </v-btn>
                </template>
              </div>

              <div class="col col-lg-5 col-xl-4">
                <template
                  v-if="
                    step.if != null && Object.keys(step.if).length !== 0 && step.if.left != null && step.if.operator != null && step.if.right != null
                  "
                >
                  <condition-box
                    :key="step.if"
                    v-model="step.if"
                    :condition="step.if"
                    :constants="constants"
                    :stageIndex="stageIndex"
                    :mode="mode"
                    @editCondition="editCondition"
                    @addChild="addChild"
                    @deleteCondition="deleteCondition"
                    @emitToggleChildren="emitToggleChildren"
                  />
                </template>
                <template v-else>
                  <v-btn block tile elevation="0" class="stepBox plain" @click="editCondition(step.if, stageIndex)" v-if="mode == 'default'">
                    <v-icon left>mdi-plus</v-icon><lang t="RULE-ADD_CONDITION" />
                  </v-btn>
                </template>
              </div>

              <div class="col col-lg-5 col-xl-4">
                <allocation-box
                  :key="step"
                  :value="step"
                  :step="step"
                  :operators="[{ name: 'default', label: 'default' }, ...ruleSettings.exprOperators]"
                  :stageIndex="stageIndex"
                  :stepIndex="stepIndex"
                  :constants="constants"
                  :mode="mode"
                  @editAllocation="editAllocation"
                  @deleteAllocation="deleteAllocation"
                />
              </div>

              <div class="col col-auto ml-auto" style="max-width: 140px;">
                <next-box :key="step" :value="step" :step="step" :stages="rules.stages" :stageIndex="stageIndex" :mode="mode" />
              </div>
            </div>

            <v-divider style="border-style: dashed;"></v-divider>
          </div>
        </draggable>

        <div class="row px-2 py-4">
          <div class="col-auto align-items">
            <v-btn v-if="mode == 'default'" elevation="0" fab x-small color="themeColor" @click="addStep(stage.steps)">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-btn class="general-action-btn" @click="addStage" v-if="mode == 'default'"> <v-icon>mdi-plus</v-icon> Stage </v-btn>
    </v-card-text>

    <v-dialog v-model="dialog.show" :width="dialog.width" :persistent="dialog.isPersistent">
      <v-card>
        <v-card-title>
          <span class="headline"><lang :t="dialog.title"></lang></span>
        </v-card-title>

        <!-- CONDITION -->
        <v-card-text class="condition" v-if="dialog.type == 'condition'">
          <v-row dense>
            <v-col cols="4" class="d-flex align-center justify-center">
              <v-menu offset-y :close-on-content-click="false" v-model="dialogConditionMenuVisible" max-height="600">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn block outlined color="secondary" v-bind="attrs" v-on="on" class="condition-menu__btn">
                    {{ dialogConditionLabel }}
                  </v-btn>
                </template>
                <v-list>
                  <v-list-group v-for="item in scopesLeft" :key="item.name">
                    <template v-slot:activator>
                      <v-list-item-content>
                        <v-list-item-title v-text="item.label ? item.label: item.name"></v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-list-item v-for="child in item.attributes" :key="child.name">
                      <v-list-item-content>
                        <v-list-item-title
                          v-text="child.label"
                          @click="setConditionDialogMenu(child.name, item.name)"
                          class="condition-menu__attribute"
                        />
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-menu>
              <v-card elevation="0" class="pa-4" style="min-height: 180px; display: flex; align-items: center; justify-content: center;">
              </v-card>
            </v-col>

            <v-col cols="2" class="d-flex align-center">
              <v-card elevation="0" outlined class="pa-4">
                <v-select :items="operators" v-model="editedCondition.operator" label="Operator" @change="changeOperator" />
              </v-card>
            </v-col>

            <v-col cols="6">
              <v-card elevation="0" outlined class="pa-4" style="min-height: 180px;">
                <template v-if="editedCondition.left.scope != null">
                  <template v-if="editedCondition.operator == 'BETWEEN' || editedCondition.operator == 'OUTSIDE'">
                    <scope-field-selector
                      :scopes="scopesRight"
                      :scope="editedCondition.right.a.scope"
                      @scope="editedCondition.right.a.scope = $event"
                      :field="editedCondition.right.a.field"
                      @field="editedCondition.right.a.field = $event"
                    >
                      <template #header>a</template>
                    </scope-field-selector>

                    <scope-field-selector
                      :scopes="scopesRight"
                      :scope="editedCondition.right.b.scope"
                      @scope="editedCondition.right.b.scope = $event"
                      :field="editedCondition.right.b.field"
                      @field="editedCondition.right.b.field = $event"
                    >
                      <template #header>b</template>
                    </scope-field-selector>
                  </template>

                  <template v-else-if="editedCondition.operator == 'IS NULL' || editedCondition.operator == 'IS NOT NULL'"> </template>

                  <template v-else-if="editedCondition.operator == 'ANY'">
                    <div class="row">
                      <div class="col">
                        <v-select :items="scopesRightEnums" v-model="editedCondition.right.scope" :item-text="(item) => item.label? item.label: item.name" item-value="name" label="Scope" />
                      </div>

                      <div class="col">
                        <v-select
                          v-if="editedCondition.right.scope"
                          :items="
                            scopesRightEnums
                              .find((s) => s.name == editedCondition.right.scope)
                              .attributes.filter((a) => a.type == 'enum' && a.options)
                          "
                          v-model="editedCondition.right.field"
                          item-text="name"
                          item-value="name"
                          label="Field"
                        />
                      </div>
                    </div>

                    <template v-if="scopeOptions && scopeOptions.length">
                      <v-select
                        :key="scopeOptions"
                        :items="scopeOptions"
                        v-model="editedCondition.right.value"
                        multiple
                        item-text="name"
                        item-value="name"
                        label="Options"
                      />
                    </template>
                  </template>

                  <template v-else>
                    <v-select :items="scopesRight" v-model="editedCondition.right.scope" :item-text="(item) => item.label ? item.label: item.name" item-value="name" label="Scope" />

                    <template v-if="editedCondition.right.scope == 'constants'">
                      <v-select
                        :items="scopesRight.find((s) => s.name == editedCondition.right.scope).attributes"
                        v-model="editedCondition.right.field"
                        :item-text="(item) => item.label? item.label: item.name"
                        item-value="id"
                        label="Konstante"
                      />
                    </template>

                    <template v-else-if="editedCondition.left.scope != null && editedCondition.right.scope == 'value'">
                      <template v-if="editedCondition.left.field != null && Object.keys(editedCondition.left.field).length !== 0">
                        <conditional-input
                          v-model="editedCondition.right.field"
                          :inputField="editedCondition.left.field"
                          :attributes="scopesLeft.find((s) => s.name == editedCondition.left.scope).attributes"
                        />
                      </template>

                      <template v-else>
                        <v-text-field v-model="editedCondition.right.field" />
                      </template>
                    </template>

                    <template v-else>
                      <v-select
                        v-if="editedCondition.right.scope != null"
                        :items="scopesRight.find((s) => s.name == editedCondition.right.scope).attributes"
                        v-model="editedCondition.right.field"
                        item-text="label"
                        item-value="name"
                        label="Attribute"
                      >
                      </v-select>
                    </template>
                  </template>
                </template>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- ALLOCATION -->
        <v-card-text v-else-if="dialog.type == 'allocation'">
          <allocation
            :ruleSettings="ruleSettings"
            :scopes="scopesRightAllocation"
            :allocation="editedAllocation"
            @update="updateAllocationData"
            :key="editedAllocation"
          />
        </v-card-text>

        <!-- STAGE NAME -->
        <v-card-text v-else-if="dialog.type == 'stage-name'">
          <v-text-field v-model="editedStageName" />
        </v-card-text>

        <!-- RULE NAME -->
        <v-card-text v-else-if="dialog.type == 'rule-name'">
          <v-text-field v-model="editedRuleName" />
        </v-card-text>

        <!-- RULE TEST -->
        <v-card-text v-else-if="dialog.type == 'rule-test'">
          <template v-if="!isGettingTestResults">
            <v-autocomplete
              clearable
              v-model="selectedBundle"
              :items="allBundles"
              :loading="isLoadingBundles"
              :search-input.sync="searchBundle"
              item-text="displayName"
              item-value="id"
              return-object
              hide-no-data
              hide-details
              :label="this.$t('PLEASE_SELECT_BUNDLE_TO_TEST_AGAINST')"
            >
            </v-autocomplete>
          </template>

          <template v-else>
            <v-container>
              <v-row class="fill-height" align-content="center" justify="center">
                <v-col class="pb-0 text-subtitle-1 text-center" cols="12">
                  <lang t="DERIVATION_RULES-IS_CALCULATING" />
                </v-col>

                <v-col cols="6" v-if="testStatusData == null">
                  <v-progress-linear color="themeColor" indeterminate rounded height="6"></v-progress-linear>
                </v-col>

                <v-col cols="12" v-else-if="testStatusData">
                  <v-progress-linear color="themeColor" rounded v-model="testStatusData.progress * 100" height="25">
                    <strong>{{ Math.ceil(testStatusData.progress * 100) }}%</strong>
                  </v-progress-linear>
                  <div class="text-center">
                    <small>Total: {{ testStatusData.total }} Index: {{ testStatusData.index }}</small>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </template>
        </v-card-text>

        <!-- DIALOG ACTIONS -->
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal" :disabled="dialog.isSaving"><lang t="GLOBAL-BTN-CANCEL"></lang></v-btn>

          <v-btn
            v-if="!isGettingTestResults"
            elevation="0"
            light
            rounded
            color="themeColor"
            :loading="isGettingTestResults || dialog.isSaving"
            @click="apply()"
          >
            <template v-if="dialog.type == 'rule-test'">
              <lang t="DERIVATION-RULE-BTN-TEST"></lang>
            </template>
            <template v-else>
              <lang t="GLOBAL-BTN-SAVE"></lang>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="testDialog.show" :width="testDialog.width" :key="testResultData">
      <div style="background-color: #FFF">
        <div ref="pdfContent">
          <rules-editor
            :calculation="testResultData"
            :testResult="testResultData"
            :ruleSettings="ruleSettings"
            :constants="constants"
            mode="testing"
          />
        </div>
        <v-card-actions>
          
          <div style="display: flex; justify-content: flex-end; width: 100%; padding-right: 1em;">
            <v-btn color="secondary" @click="generatePDFReport()"><lang t="EXPORT-PDF"></lang></v-btn>
            <v-btn text @click="closeModalTest()"><lang t="GLOBAL-BTN-CLOSE"></lang></v-btn>
          </div>
        </v-card-actions>
      </div>
    </v-dialog>
  </v-card>
</template>

<script>
import ConditionalInput from "./RulesEditorConditionalInput";
import Allocation from "./RulesEditor-Allocation";
import conditionBox from "./conditionBox";
import allocationBox from "./allocationBox";
import nextBox from "./nextBox";
import scopeFieldSelector from "./ScopeFieldSelector";
import { cloneDeep } from "lodash";
import HelpTextBtn from "../../../components/HelpTextBtn.vue";
import { createPDF } from "@/utilities/pdfs";
import draggable from "vuedraggable";

export default {
  name: "rulesEditor",

  components: {
    ConditionalInput,
    Allocation,
    conditionBox,
    allocationBox,
    nextBox,
    scopeFieldSelector,
    HelpTextBtn,
    draggable,
  },

  props: {
    calculation: {
      type: Object,
      default: null,
    },
    ruleSettings: {
      type: Object,
      default: null,
    },
    constants: {
      type: Array,
      default: null,
    },
    testResult: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "default",
    },
  },

  data() {
    return {
      isSaving: false,
      isPublishing: false,
      isLoading: false,
      rules: null,
      dialogConditionMenuVisible: false,
      dialogConditionLabel: "",
      operators: ["=", ">", "<", "<>", ">=", "<=", "BETWEEN", "OUTSIDE", "ANY", "IS NULL", "IS NOT NULL"],
      scopeLeftAttribute: "",
      dialog: {
        show: false,
        width: "640px",
        title: "",
        type: null,
        isSaving: false,
        isPersistent: true,
      },

      testDialog: {
        show: false,
        width: "840px",
        title: "",
        type: null,
        isSaving: false,
        isPersistent: false,
      },
      testResultData: {},

      currentStage: {},
      currentCondition: null,
      currentAllocation: null,

      currentStageIndex: null,

      editedStageName: null,
      editedRuleName: null,
      editedCondition: {
        left: {
          field: null,
          scope: null,
        },
        right: {
          scope: null,
          value: null,
        },
        operator: "",
        children: [],
      },

      defaultCondition: {
        left: {
          field: null,
          scope: null,
        },
        right: {
          scope: null,
          value: null,
        },
        operator: "=",
        children: [],
      },

      editedAllocation: {
        scope: null,
        field: null,
        ternary: null,
        fork: null,
        expr: null,
      },

      defaultAllocation: {
        scope: null,
        field: null,
        ternary: null,
        fork: null,
        expr: null,
      },

      selectedBundle: null,
      searchBundle: null,
      isLoadingBundles: false,

      bundles: [
        {
          name: "All Bundles",
          displayName: 'All Bundles',
          id: null,
        },
      ],

      isGettingTestResults: false,
      searchIsTouched: false,
      testStatusData: null,
    };
  },

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },

    "editedCondition.left.scope"(newVal, oldVal) {
      if (oldVal != null) {
        this.editedCondition.left.field = null;
      }
    },

    "editedCondition.right.field"(newVal, oldVal) {
      if (this.editedCondition.operator == "ANY" && typeof oldVal != "undefined" && newVal != oldVal) {
        this.editedCondition.right.value = [];
      }
    },

    scopeLeftAttribute(newValue) {
      this.editedCondition.left.field = newValue;
    },

    searchBundle(val) {
      if(!val || val === this.selectedBundle) return;
      this.queryBundlesDebounced(val);
    },

  },

  computed: {
    currentUserRole(){
      const user = this.$store.getters['currentUser'];
      if(!user) return null;
      return user.role;
    },

    allBundles(){
      return this.bundles;
    }, 

    scopesLeft() {
      if (this.currentStageIndex > 0) {
        return [
          {
            name: "Stage Input",
            attributes: [
            {label: "Stage Input", name: "Stage Input", type: "string"}],
          },
          ...this.ruleSettings.scopes,
        ];
      } else {
        return this.ruleSettings.scopes;
      }
    },

    scopeLeftWithOptions() {
      const list = [];
      for (const scope of this.scopesLeft) {
        const label = scope.name;
        const value = scope.name;
        const disabled = false;
        const selectable = false;
        const options = scope.attributes.map((el) => {
          return {
            label: el.label,
            value: el.name,
          };
        });
        list.push({ label, value, selectable, disabled, options });
      }
      return list;
    },

    scopeLeftAttributeName() {
      return (scopeName, attributeValue) => {
        if (!scopeName || !attributeValue) return "";
        const findScope = this.scopesLeft.find((el) => el.name === scopeName);
        if (!findScope) throw new Error("Cannot find scope");
        const attrName = findScope.attributes.find((el) => el.name === attributeValue);
        if (!attrName) throw new Error("Attribute cannot be found");
        return attrName.label;
      };
    },

    scopesRight() {
      return [
        {
          name: "constants",
          attributes: this.constants,
        },
        {
          name: "value",
          attributes: [],
        },
        ...this.ruleSettings.scopes,
      ];
    },

    scopesRightAllocation() {
      if (this.currentStageIndex > 0) {
        let scopes = this.scopesRight;
        scopes.unshift({
          name: "Stage Input",
          attributes: [{label: "Stage Input", name: "Stage Input", type: "string"}],
        });

        return scopes;
      } else {
        return this.scopesRight;
      }
    },

    scopesRightEnums() {
      return this.ruleSettings.scopes.filter((scope) => scope.attributes.filter((a) => a.type == "enum" && a.options).length > 0);
    },

    scopeOptions() {
      let foundIndex = this.scopesRight.findIndex((scope) => scope.name == this.editedCondition.right.scope);
      if (foundIndex > -1) {
        let foundIndex2 = this.scopesRight[foundIndex].attributes.findIndex((a) => a.name == this.editedCondition.right.field);
        if (foundIndex2 > -1) {
          if (
            this.scopesRight[foundIndex].attributes[foundIndex2].type == "enum" &&
            this.scopesRight[foundIndex].attributes[foundIndex2].options.length
          ) {
            return this.scopesRight[foundIndex].attributes[foundIndex2].options.map((option, index) => {
              return { name: option, value: option };
            });
          }
        }
      }

      return [];
    },
  },

  created() {
    this.$store.commit("RESET_TOGGLE_CONDITION_BOX_LIST");

    let rules;
    if (this.mode == "default") {
      rules = this.calculation.settings;
    } else {
      rules = this.testResult.explanation;
    }

    this.rules = rules;
    const userRole = this.currentUserRole;
    if(!['adm', 'su'].includes(userRole)){
      if(this.testResult === null){
        this.testResult = cloneDeep(this.calculation);
      }
      this.mode= 'testing';
            
    }

  },

  methods: {

    setConditionDialogMenu(attribute, field) {
      this.scopeLeftAttribute = attribute;
      this.editedCondition.left.scope = field;
      this.editedCondition.left.field = attribute;
      this.updateDialogConditionLabel(field, attribute);
      this.dialogConditionMenuVisible = false;
    },

    generatePDFReport() {
      createPDF(this.$refs.pdfContent, "report");
    },
    queryBundlesDebounced(val) {
      // cancel pending call
      clearTimeout(this._timerId);

      // delay new call 500ms
      this._timerId = setTimeout(() => {
        this.queryBundles(val);
      }, 500);
    },

    queryBundles(val) {
      this.isLoadingBundles = true;

      this.$axios
        .post("bundles/query", { query: val })
        .then((res) => {
          this.bundles = [
            {
              name: "All Bundles",
              displayName: 'All Bundles',
              id: null,
            },
            ...res.data.map((el) => {
              let additionalInfoList = [];
              let level = '';
              if(el.level){
                level = `${String(el.level).toUpperCase()}`
              }

              if(el.articleNo){
                additionalInfoList.push(`${el.articleNo}`);
              }
              if(el.gtin){
                additionalInfoList.push(`${el.gtin}`);
              }
              if(additionalInfoList.length > 0 || level){
                let info = ``;
                if(level){
                  info = `(${level}) `;
                }
                if(additionalInfoList.length > 0){
                  info += `(${additionalInfoList.join(', ')})`;
                }
                el.displayName = `${el.name} ${info}`
              }else{
                el.displayName = el.name;
              }
              return el;
            }),
          ];
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => (this.isLoadingBundles = false));
    },

    changeOperator() {
      switch (this.editedCondition.operator) {
        case "OUTSIDE":
        case "BETWEEN":
          this.editedCondition.right = {
            a: {
              scope: null,
              field: null,
            },
            b: {
              scope: null,
              field: null,
            },
          };
          break;

        case "ANY":
          this.editedCondition.right = {
            scope: null,
            field: [],
            value: [],
          };
          break;

        case "IS NULL":
        case "IS NOT NULL":
          this.editedCondition.right = {};
          break;

        default:
          this.editedCondition.right = {
            scope: null,
            field: null,
          };
      }
    },

    updateAllocationData(allocation) {
      this.editedAllocation = allocation;
      this.$forceUpdate();
    },

    deleteStage(stageIndex) {

      this.rules.stages.splice(stageIndex, 1);
      this.$forceUpdate();
    },

    resetNext(step, stageIndex) {
      if (step.next == stageIndex) {
        step.next = null;
      }

      if (step.next != null && step.next > stageIndex) {
        step.next = step.next - 1;
      }

      if (step.if.children.length > 0) {
        step.if.children.forEach((ch) => {
          this.resetNext(ch, stageIndex);
        });
      }
    },

    changeRuleName() {
      this.editedRuleName = this.calculation.name;

      this.dialog.title = this.$t("DERIVATION_RULE-CHANGE_RULE_NAME");
      this.dialog.width = "480px";
      this.dialog.type = "rule-name";
      this.dialog.show = true;
    },

    changeStageName(stage) {
      this.currentStage = stage;

      this.editedStageName = stage.name;
      this.dialog.title = this.$t("DERIVATION_RULE-CHANGE_STAGE_NAME");
      this.dialog.width = "480px";
      this.dialog.type = "stage-name";
      this.dialog.show = true;
    },

    addStage() {
      this.rules.stages.push({
        name: "new stage " + (this.rules.stages.length + 1),
        steps: [],
      });
    },

    addStep(steps) {
      steps.push({
        if: {},
        then: {},
        next: null,
      });
    },

    addChild(condition) {
      if (!condition.children) {
        this.$set(condition, "children", []);
      }

      condition.children.push({
        if: JSON.parse(JSON.stringify(this.defaultCondition)),
        then: JSON.parse(JSON.stringify(this.defaultAllocation)),
        next: null,
      });

      this.$forceUpdate();
    },

    deleteStep(steps, stepIndex) {
      steps.splice(stepIndex, 1);
    },

    editCondition(condition, stageIndex) {
      this.currentStageIndex = stageIndex;
      this.currentCondition = condition;

      if (
        condition == null ||
        Object.keys(condition).length === 0 ||
        (condition.left == null && condition.right == null && condition.operator == null)
      ) {
        this.editedCondition = JSON.parse(JSON.stringify(this.defaultCondition));
      } else {
        this.editedCondition = JSON.parse(JSON.stringify(condition));
      }

      this.dialog.title = this.$t("DERIVATION_RULE-CONDITION");
      this.dialog.type = "condition";
      this.dialog.width = "1024px";
      this.dialog.show = true;
      this.scopeLeftAttribute = this.editedCondition.left.field;

      const { scope, field } = this.editedCondition.left;
      this.updateDialogConditionLabel(scope, field);
    },

    updateDialogConditionLabel(scope, field) {
      const scopeLeftAttributeName = this.scopeLeftAttributeName(scope, field);

      this.dialogConditionLabel = scopeLeftAttributeName || this.$t("SCOPE");
    },

    editAllocation(allocation, stageIndex) {
      this.currentStageIndex = stageIndex;
      this.currentAllocation = allocation;

      if (allocation == null || Object.keys(allocation).length === 0 || (allocation.field == null && allocation.scope == null)) {
        this.editedAllocation = JSON.parse(JSON.stringify(this.defaultAllocation));
      } else {
        this.editedAllocation = { ...allocation };
      }

      this.dialog.title = this.$t("DERIVATION_RULE-ALLOCATION");
      this.dialog.type = "allocation";
      this.dialog.width = "840px";

      this.dialog.show = true;
    },

    deleteCondition(condition, parent, index) {
      if (!parent && !index) {
        condition.operator = null;
        condition.left = null;
        condition.right = null;
        condition.children = [];
      } else {
        parent.children.splice(index, 1);
      }

      this.$forceUpdate();
    },

    emitToggleChildren(condition, status) {
        this.$store.commit("TOGGLE_CONDITION_BOX_LIST", { condition, status });
    },

    deleteAllocation(condition, parent, index) {
      if (!parent && !index) {
        condition.then = JSON.parse(JSON.stringify(this.defaultAllocation));
      } else {
        parent.if.children[index].then = JSON.parse(JSON.stringify(this.defaultAllocation));
      }

      this.$forceUpdate();
    },

    closeModal() {
      this.dialog.show = false;

      this.$nextTick(() => {
        this.currentCondition = null;
        this.editedCondition = Object.assign({}, this.defaultCondition);

        this.currentAllocation = null;
        this.editedAllocation = Object.assign({}, this.defaultAllocation);

        this.currentStageIndex = null;
        this.editedStageName = null;
        this.editedRuleName = null;
        this.isGettingTestResults = false;
        this.testStatusData = null;
      });
    },

    closeModalTest() {
      this.testDialog.show = false;
      this.selectedBundle = null;
    },

    apply() {
      if (this.dialog.type == "condition") {
        if (!this.editedCondition.operator) {
          this.$store.dispatch("setSnackbar", {
            text: "please add an operator for this condition",
            type: "warning",
          });
          return;
        }

        this.currentCondition.operator = this.editedCondition.operator;
        this.currentCondition.left = this.editedCondition.left;
        this.currentCondition.right = this.editedCondition.right;
      } else if (this.dialog.type == "allocation") {
        this.$set(this.currentAllocation, "scope", this.editedAllocation.scope);
        this.$set(this.currentAllocation, "field", this.editedAllocation.field);

        // reset others to null (may be filled below)
        this.$set(this.currentAllocation, "fork", null);
        this.$set(this.currentAllocation, "ternary", null);
        this.$set(this.currentAllocation, "expr", null);

        // apply fork
        if (this.editedAllocation.fork) {
          this.$set(this.currentAllocation, "ternary", null);
          this.$set(this.currentAllocation, "expr", null);
          this.$set(this.currentAllocation, "fork", this.editedAllocation.fork);
          this.$set(this.currentAllocation.fork, "lowerLimit", this.editedAllocation.fork.lowerLimit);
          this.$set(this.currentAllocation.fork, "upperLimit", this.editedAllocation.fork.upperLimit);
        }

        // apply ternary
        if (this.editedAllocation.ternary) {
          this.$set(this.currentAllocation, "fork", null);
          this.$set(this.currentAllocation, "expr", null);
          this.$set(this.currentAllocation, "ternary", this.editedAllocation.ternary);
        }

        // apply expression
        if (this.editedAllocation.expr) {
          this.$set(this.currentAllocation, "fork", null);
          this.$set(this.currentAllocation, "ternary", null);
          this.$set(this.currentAllocation, "expr", this.editedAllocation.expr);
          this.$set(this.currentAllocation.expr, "terms", this.editedAllocation.expr.terms);
        }
      } else if (this.dialog.type == "stage-name") {
        this.currentStage.name = this.editedStageName;
      } else if (this.dialog.type == "rule-name") {
        this.calculation.name = this.editedRuleName;
      } else if (this.dialog.type == "rule-test") {
        this.ruleSave({ isSilent: true }).then((res) => {
          this._ruleTest();
        });
      }

      if (this.dialog.type != "rule-test") {
        this.$forceUpdate();
        this.closeModal();
      }
    },

    ruleTest() {
      this.dialog.title = this.$t("DERIVATION_RULE-TEST");
      this.dialog.width = "640px";
      this.dialog.type = "rule-test";
      this.dialog.show = true;
    },

    _ruleTest() {
      this.isGettingTestResults = true;

      let bundleId = this.selectedBundle != null ? this.selectedBundle.id : null;

      this.$axios
        .post("calculationrules/" + this.calculation.id + "/test", {
          bundleId: bundleId,
        })
        .then((res) => {
          if (res.data.statusPath) {
            this.waitForTestResult(res.data.statusPath);
          } else {
            this.testResultData = res.data;
            this.testDialog.title = this.$t("DERIVATION_RULE-TEST-RESULT");
            this.testDialog.width = "92%";
            this.testDialog.type = "testing";

            // wait some seconds before showing the test result
            setTimeout(() => {
              this.closeModal();
              this.testDialog.show = true;
            }, 1200);
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$store.dispatch("setSnackbar", {
              text: error.response.statusText + ": " + error.response.data.message,
              type: "error",
            });
          }
        })
        .finally(() => {
          this.isGettingTestResult = false;
        });
    },

    waitForTestResult(statusPath) {
      if (!this.isGettingTestResults) {
        return;
      }

      this.$axios.post("/calculationrules/testStatus", { statusPath: statusPath }).then((res) => {
        this.testStatusData = res.data.status;

        if (res.data.status.progress != 1) {
          setTimeout(() => {
            this.waitForTestResult(statusPath);
          }, 500);
        } else {
          this.testResultData = res.data;
          this.closeModal();

          this.testDialog.title = this.$t("DERIVATION_RULE-TEST-RESULT");
          this.testDialog.width = "92%";
          this.testDialog.type = "testing";
          this.testDialog.show = true;
        }
      });
    },

    ruleSave(args = false) {
      let isSilent = args && args.isSilent;
      this.isSaving = true;

      if (this.calculation.id) {
        return this.$axios.put("calculationrules/" + this.calculation.id, this.calculation).then((res) => {
          setTimeout(() => {
            this.isSaving = false;
            if (!isSilent) {
              this.$store.dispatch("setSnackbar", {
                text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED"),
              });
            }
          }, 350);
        });
      } else {
        return this.$axios.post("calculationrules", this.calculation).then((res) => {
          this.isSaving = false;
          this.calculation.id = res.data.id;
          if (!isSilent) {
            this.$store.dispatch("setSnackbar", {
              text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED"),
            });
          }
        });
      }
    },

    rulePublish() {
      this.isPublishing = true;

      this.$axios.get("calculationrules/" + this.calculation.id + "/publish").then((res) => {
        this.isPublishing = false;
        this.$store.dispatch("setSnackbar", {
          text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_PUBLISHED"),
        });

        this.$emit("closeModal");
      });
    },
  },
};
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.testResultBoxWrapper {
  display: flex;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;

  .testResultBox {
    border-radius: 4px;
    background-color: #f0f0f0;
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    margin: 5px 5px 0 0;
    padding: 4px;
    min-width: 180px;

    div {
      width: 50%;
      text-align: center;
      font-size: 12px;
      line-height: 1.1rem;
      flex-grow: 1;

      &:first-child {
        font-weight: bold;
        border-right: 1px solid #ddd;
      }
    }
  }
}
.stage {
  .stageHeader {
    border-bottom: 1px solid lightgrey;

    .stageName {
      cursor: pointer;
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  .row {
    .stepBox {
      background-color: #f0f0f0;
      position: relative;
      text-align: center;
      border-radius: 4px;
      /* min-height: 95px; */
      min-height: 115px;
      text-transform: uppercase;

      &.is-hidden {
        visibility: hidden;
      }

      &.not-passed {
        /*background-color: map-get($red, lighten-2);*/
        background-color: rgba(156, 50, 38, 0.2);
        .col.operator {
          background-color: rgba(156, 50, 38, 0.1) !important;
        }
      }

      &.passed {
        background-color: rgba(170, 216, 172, 0.35);
        .col.operator {
          background-color: rgba(170, 216, 172, 0.3) !important;
        }
      }

      .actions {
        display: block;
        width: 100%;
        position: absolute;
        top: 0;
        right: 0;
        text-align: right;
      }

      .passedPercentage {
        position: absolute;
        z-index: 20;
        top: 4px;
        right: 6px;

        span {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          width: 28px;
          height: 28px;

          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
        }
      }

      .stepError {
        position: absolute;
        right: 6px;
        top: 4px;
      }

      .container {
        > .row {
          font-size: 12px;
          line-height: 1rem;
          min-height: 115px;
          position: relative;
          flex-wrap: nowrap;

          > .col {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 100px;

            .scopeField {
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 4;
              white-space: normal;
            }

            &.operator {
              background-color: #eaeaea;
              max-width: 52px;
              min-width: 52px;

              div {
                line-height: 1rem;
                overflow: hidden;
                font-weight: bold;
                font-size: 12px;
                word-break: break-all;
              }
            }

            .constant {
              text-transform: none;
              .c {
                font-weight: bold;
                font-style: italic;
                margin-right: 4px;
              }
            }
          }
        }
      }

      .addSubconditionBtn {
        position: absolute;
        right: 0;
        bottom: 0;
      }
      &.plain {
        border: 2px dashed lightgrey;
        color: #444;
      }
    }
  }
}

.condition {
  .row {
  }
}

.condition-menu {
  &__btn {
    border-color: rgba(0, 0, 0, 0.12);
  }

  &__attribute {
    cursor: pointer;
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 12px !important;
    line-height: 13.8px !important;
  }
}
</style>
